/**
 * ─── Philippine Government Contributions Calculator ──────────
 * Computes SSS, PhilHealth, and Pag-IBIG contributions based on
 * current mandated rates (2025).
 *
 * All rates are metadata-driven through PayrollConfig — tenants
 * can update rates when government mandates change without
 * code deployment.
 */

import type { PayrollConfig, Employee } from './hris.schema'
import { DEFAULT_PAYROLL_CONFIG } from './hris.schema'

// ─── SSS Contribution Table (2025) ──────────────────────────
// Based on SSS Circular 2024-001: 14% total rate
// Employee share: 4.5% of Monthly Salary Credit (MSC)
// Employer share: 9.5% of MSC
// MSC ranges from 4,000 to 30,000 (2025)

interface SSSBracket {
  salaryFrom: number
  salaryTo: number
  msc: number          // Monthly Salary Credit
  employeeShare: number
  employerShare: number
}

/** SSS Contribution Schedule — metadata-driven, configurable */
const SSS_CONTRIBUTION_TABLE: SSSBracket[] = [
  { salaryFrom: 0, salaryTo: 4249.99, msc: 4000, employeeShare: 180, employerShare: 380 },
  { salaryFrom: 4250, salaryTo: 4749.99, msc: 4500, employeeShare: 202.50, employerShare: 427.50 },
  { salaryFrom: 4750, salaryTo: 5249.99, msc: 5000, employeeShare: 225, employerShare: 475 },
  { salaryFrom: 5250, salaryTo: 5749.99, msc: 5500, employeeShare: 247.50, employerShare: 522.50 },
  { salaryFrom: 5750, salaryTo: 6249.99, msc: 6000, employeeShare: 270, employerShare: 570 },
  { salaryFrom: 6250, salaryTo: 6749.99, msc: 6500, employeeShare: 292.50, employerShare: 617.50 },
  { salaryFrom: 6750, salaryTo: 7249.99, msc: 7000, employeeShare: 315, employerShare: 665 },
  { salaryFrom: 7250, salaryTo: 7749.99, msc: 7500, employeeShare: 337.50, employerShare: 712.50 },
  { salaryFrom: 7750, salaryTo: 8249.99, msc: 8000, employeeShare: 360, employerShare: 760 },
  { salaryFrom: 8250, salaryTo: 8749.99, msc: 8500, employeeShare: 382.50, employerShare: 807.50 },
  { salaryFrom: 8750, salaryTo: 9249.99, msc: 9000, employeeShare: 405, employerShare: 855 },
  { salaryFrom: 9250, salaryTo: 9749.99, msc: 9500, employeeShare: 427.50, employerShare: 902.50 },
  { salaryFrom: 9750, salaryTo: 10249.99, msc: 10000, employeeShare: 450, employerShare: 950 },
  { salaryFrom: 10250, salaryTo: 10749.99, msc: 10500, employeeShare: 472.50, employerShare: 997.50 },
  { salaryFrom: 10750, salaryTo: 11249.99, msc: 11000, employeeShare: 495, employerShare: 1045 },
  { salaryFrom: 11250, salaryTo: 11749.99, msc: 11500, employeeShare: 517.50, employerShare: 1092.50 },
  { salaryFrom: 11750, salaryTo: 12249.99, msc: 12000, employeeShare: 540, employerShare: 1140 },
  { salaryFrom: 12250, salaryTo: 12749.99, msc: 12500, employeeShare: 562.50, employerShare: 1187.50 },
  { salaryFrom: 12750, salaryTo: 13249.99, msc: 13000, employeeShare: 585, employerShare: 1235 },
  { salaryFrom: 13250, salaryTo: 13749.99, msc: 13500, employeeShare: 607.50, employerShare: 1282.50 },
  { salaryFrom: 13750, salaryTo: 14249.99, msc: 14000, employeeShare: 630, employerShare: 1330 },
  { salaryFrom: 14250, salaryTo: 14749.99, msc: 14500, employeeShare: 652.50, employerShare: 1377.50 },
  { salaryFrom: 14750, salaryTo: 15249.99, msc: 15000, employeeShare: 675, employerShare: 1425 },
  { salaryFrom: 15250, salaryTo: 15749.99, msc: 15500, employeeShare: 697.50, employerShare: 1472.50 },
  { salaryFrom: 15750, salaryTo: 16249.99, msc: 16000, employeeShare: 720, employerShare: 1520 },
  { salaryFrom: 16250, salaryTo: 16749.99, msc: 16500, employeeShare: 742.50, employerShare: 1567.50 },
  { salaryFrom: 16750, salaryTo: 17249.99, msc: 17000, employeeShare: 765, employerShare: 1615 },
  { salaryFrom: 17250, salaryTo: 17749.99, msc: 17500, employeeShare: 787.50, employerShare: 1662.50 },
  { salaryFrom: 17750, salaryTo: 18249.99, msc: 18000, employeeShare: 810, employerShare: 1710 },
  { salaryFrom: 18250, salaryTo: 18749.99, msc: 18500, employeeShare: 832.50, employerShare: 1757.50 },
  { salaryFrom: 18750, salaryTo: 19249.99, msc: 19000, employeeShare: 855, employerShare: 1805 },
  { salaryFrom: 19250, salaryTo: 19749.99, msc: 19500, employeeShare: 877.50, employerShare: 1852.50 },
  { salaryFrom: 19750, salaryTo: 20249.99, msc: 20000, employeeShare: 900, employerShare: 1900 },
  { salaryFrom: 20250, salaryTo: 20749.99, msc: 20500, employeeShare: 922.50, employerShare: 1947.50 },
  { salaryFrom: 20750, salaryTo: 21249.99, msc: 21000, employeeShare: 945, employerShare: 1995 },
  { salaryFrom: 21250, salaryTo: 21749.99, msc: 21500, employeeShare: 967.50, employerShare: 2042.50 },
  { salaryFrom: 21750, salaryTo: 22249.99, msc: 22000, employeeShare: 990, employerShare: 2090 },
  { salaryFrom: 22250, salaryTo: 22749.99, msc: 22500, employeeShare: 1012.50, employerShare: 2137.50 },
  { salaryFrom: 22750, salaryTo: 23249.99, msc: 23000, employeeShare: 1035, employerShare: 2185 },
  { salaryFrom: 23250, salaryTo: 23749.99, msc: 23500, employeeShare: 1057.50, employerShare: 2232.50 },
  { salaryFrom: 23750, salaryTo: 24249.99, msc: 24000, employeeShare: 1080, employerShare: 2280 },
  { salaryFrom: 24250, salaryTo: 24749.99, msc: 24500, employeeShare: 1102.50, employerShare: 2327.50 },
  { salaryFrom: 24750, salaryTo: 25249.99, msc: 25000, employeeShare: 1125, employerShare: 2375 },
  { salaryFrom: 25250, salaryTo: 25749.99, msc: 25500, employeeShare: 1147.50, employerShare: 2422.50 },
  { salaryFrom: 25750, salaryTo: 26249.99, msc: 26000, employeeShare: 1170, employerShare: 2470 },
  { salaryFrom: 26250, salaryTo: 26749.99, msc: 26500, employeeShare: 1192.50, employerShare: 2517.50 },
  { salaryFrom: 26750, salaryTo: 27249.99, msc: 27000, employeeShare: 1215, employerShare: 2565 },
  { salaryFrom: 27250, salaryTo: 27749.99, msc: 27500, employeeShare: 1237.50, employerShare: 2612.50 },
  { salaryFrom: 27750, salaryTo: 28249.99, msc: 28000, employeeShare: 1260, employerShare: 2660 },
  { salaryFrom: 28250, salaryTo: 28749.99, msc: 28500, employeeShare: 1282.50, employerShare: 2707.50 },
  { salaryFrom: 28750, salaryTo: 29249.99, msc: 29000, employeeShare: 1305, employerShare: 2755 },
  { salaryFrom: 29250, salaryTo: 29749.99, msc: 29500, employeeShare: 1327.50, employerShare: 2802.50 },
  { salaryFrom: 29750, salaryTo: Infinity, msc: 30000, employeeShare: 1350, employerShare: 2850 },
]

// ═══════════════════════════════════════════════════════════════
// Contribution Service
// ═══════════════════════════════════════════════════════════════

export class ContributionService {
  private config: PayrollConfig

  constructor(config: PayrollConfig = DEFAULT_PAYROLL_CONFIG) {
    this.config = config
  }

  /**
   * Get SSS contribution for a given monthly salary.
   * Returns employee share only (employer share computed separately).
   */
  static getSSSContribution(monthlySalary: number): {
    msc: number
    employeeShare: number
    employerShare: number
  } {
    for (const bracket of SSS_CONTRIBUTION_TABLE) {
      if (monthlySalary <= bracket.salaryTo) {
        return {
          msc: bracket.msc,
          employeeShare: bracket.employeeShare,
          employerShare: bracket.employerShare,
        }
      }
    }
    // Max bracket
    const max = SSS_CONTRIBUTION_TABLE[SSS_CONTRIBUTION_TABLE.length - 1]!
    return {
      msc: max.msc,
      employeeShare: max.employeeShare,
      employerShare: max.employerShare,
    }
  }

  /**
   * Compute PhilHealth contribution (2025: 5% of monthly salary, split equally).
   * Salary floor: 10,000 PHP, Salary ceiling: 100,000 PHP.
   */
  static getPhilHealthContribution(monthlySalary: number): {
    employeeShare: number
    employerShare: number
  } {
    const base = Math.max(10000, Math.min(100000, monthlySalary))
    const total = base * 0.05
    const half = Math.round(total / 2 * 100) / 100
    return {
      employeeShare: half,
      employerShare: half,
    }
  }

  /**
   * Compute Pag-IBIG contribution.
   * Employee: 100 if salary ≤ 1,500, else 200
   * Employer: 100 or 200 (matching)
   */
  static getPagIBIGContribution(
    monthlySalary: number,
    config: PayrollConfig = DEFAULT_PAYROLL_CONFIG,
  ): { employeeShare: number; employerShare: number } {
    const contribution = monthlySalary <= config.pagibigSalaryThreshold
      ? config.pagibigMinEmployeeContribution
      : config.pagibigMaxEmployeeContribution
    return {
      employeeShare: contribution,
      employerShare: contribution,
    }
  }

  /**
   * Compute all three government contributions at once.
   */
  static computeAllContributions(
    monthlySalary: number,
    config: PayrollConfig = DEFAULT_PAYROLL_CONFIG,
  ): {
    sss: { employeeShare: number; employerShare: number; msc: number }
    philhealth: { employeeShare: number; employerShare: number }
    pagibig: { employeeShare: number; employerShare: number }
    totalEmployeeShare: number
    totalEmployerShare: number
  } {
    const sss = ContributionService.getSSSContribution(monthlySalary)
    const philhealth = ContributionService.getPhilHealthContribution(monthlySalary)
    const pagibig = ContributionService.getPagIBIGContribution(monthlySalary, config)

    return {
      sss: { employeeShare: sss.employeeShare, employerShare: sss.employerShare, msc: sss.msc },
      philhealth,
      pagibig,
      totalEmployeeShare: sss.employeeShare + philhealth.employeeShare + pagibig.employeeShare,
      totalEmployerShare: sss.employerShare + philhealth.employerShare + pagibig.employerShare,
    }
  }

  /**
   * Compute overtime pay per DOLE labor code.
   *
   * @param hourlyRate — Employee's hourly rate (basicSalary / 26 days / 8 hours)
   * @param overtimeHours — Number of overtime hours
   * @param rateMultiplier — e.g. 1.25 for regular OT, 1.30 for rest day, 2.00 for holiday
   */
  static computeOvertimePay(
    hourlyRate: number,
    overtimeHours: number,
    rateMultiplier: number = 1.25,
  ): number {
    return Math.round(hourlyRate * overtimeHours * rateMultiplier * 100) / 100
  }

  /**
   * Compute night shift differential (10pm-6am).
   * Additional 10% of hourly rate per hour worked between 10pm-6am.
   */
  static computeNightDifferential(
    hourlyRate: number,
    nightHours: number,
    config: PayrollConfig = DEFAULT_PAYROLL_CONFIG,
  ): number {
    return Math.round(hourlyRate * nightHours * config.nightDiffRate * 100) / 100
  }

  /**
   * Compute daily rate from monthly salary.
   * Standard: monthly / 26 (for daily-paid) or / 22 (for monthly-paid with weekends off)
   */
  static getDailyRate(monthlySalary: number, workingDaysPerMonth: number = 26): number {
    return Math.round(monthlySalary / workingDaysPerMonth * 100) / 100
  }

  /**
   * Compute hourly rate from monthly salary.
   */
  static getHourlyRate(monthlySalary: number, workingDaysPerMonth: number = 26, hoursPerDay: number = 8): number {
    return Math.round(monthlySalary / workingDaysPerMonth / hoursPerDay * 100) / 100
  }

  /**
   * Compute pro-rated salary for a partial pay period.
   * Used for new hires or resigned employees mid-period.
   */
  static computeProRatedSalary(
    monthlySalary: number,
    daysWorked: number,
    totalWorkingDaysInPeriod: number = 13,
  ): number {
    if (totalWorkingDaysInPeriod <= 0) return 0
    return Math.round(monthlySalary * (daysWorked / totalWorkingDaysInPeriod) * 100) / 100
  }

  /**
   * Compute late/absence deduction.
   */
  static computeLateDeduction(
    hourlyRate: number,
    lateMinutes: number,
  ): number {
    return Math.round(hourlyRate * (lateMinutes / 60) * 100) / 100
  }

  /**
   * Format payslip as printable text.
   */
  static formatPayslip(payslip: {
    employeeName: string
    employeeCode: string
    department: string
    position: string
    periodStart: string
    periodEnd: string
    basicPay: number
    overtimePay: number
    holidayPay: number
    nightDifferential: number
    allowance: number
    otherEarnings: number
    grossPay: number
    sssContribution: number
    philhealthContribution: number
    pagibigContribution: number
    withholdingTax: number
    sssLoan: number
    pagibigLoan: number
    cashAdvances: number
    lateDeduction: number
    absenceDeduction: number
    otherDeductions: number
    totalDeductions: number
    netPay: number
    daysWorked: number
    hoursWorked: number
    overtimeHours: number
    lateMinutes: number
  }): string {
    const nf = (n: number) => `₱${n.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`

    return `
═══════════════════════════════════════════════════════════════
                       P A Y S L I P
                    ${payslip.periodStart} to ${payslip.periodEnd}
═══════════════════════════════════════════════════════════════

Employee:     ${payslip.employeeName} (${payslip.employeeCode})
Department:   ${payslip.department}
Position:     ${payslip.position}

───────────────────────────────────────────────────────────────
  EARNINGS
───────────────────────────────────────────────────────────────
  Basic Pay:              ${nf(payslip.basicPay)}
  Overtime Pay:           ${nf(payslip.overtimePay)}
  Holiday Pay:            ${nf(payslip.holidayPay)}
  Night Differential:     ${nf(payslip.nightDifferential)}
  Allowance:              ${nf(payslip.allowance)}
  Other Earnings:         ${nf(payslip.otherEarnings)}
───────────────────────────────────────────────────────────────
  GROSS PAY:              ${nf(payslip.grossPay)}
───────────────────────────────────────────────────────────────

───────────────────────────────────────────────────────────────
  DEDUCTIONS
───────────────────────────────────────────────────────────────
  SSS Contribution:       ${nf(payslip.sssContribution)}
  PhilHealth:             ${nf(payslip.philhealthContribution)}
  Pag-IBIG:               ${nf(payslip.pagibigContribution)}
  Withholding Tax:        ${nf(payslip.withholdingTax)}
  SSS Loan:               ${nf(payslip.sssLoan)}
  Pag-IBIG Loan:          ${nf(payslip.pagibigLoan)}
  Cash Advances:          ${nf(payslip.cashAdvances)}
  Late:                   ${nf(payslip.lateDeduction)}
  Absences:               ${nf(payslip.absenceDeduction)}
  Other Deductions:       ${nf(payslip.otherDeductions)}
───────────────────────────────────────────────────────────────
  TOTAL DEDUCTIONS:       ${nf(payslip.totalDeductions)}
───────────────────────────────────────────────────────────────

───────────────────────────────────────────────────────────────
  NET PAY:                ${nf(payslip.netPay)}
───────────────────────────────────────────────────────────────

Attendance:  ${payslip.daysWorked} days · ${payslip.hoursWorked} hrs
             OT: ${payslip.overtimeHours} hrs · Late: ${payslip.lateMinutes} min

═══════════════════════════════════════════════════════════════
`.trim()
  }
}
